// The identity row, the same on every screen: the viewer's hologram at one
// scale on the left, and beside it the name in the game's type, a standing
// line, and a status line that says what is happening. It is the one place
// the page says who you are and where things stand, so no screen has to.

import { llamaTag } from '../protocol';
import { el } from './dom';
import { hueColor } from './hues';
import type { Page } from './page';
import { pixelText } from './pixelText';
import { say } from './say';
import type { Screen } from './state';
import { STRINGS } from './strings';

const NAME_SCALE = 2;

export function identityRow(page: Page, screen: Screen, scale: number): HTMLElement {
  const state = page.state();
  const color = hueColor(state.hue);
  const row = el('div', 'id');
  page.hologram.setColor(color);
  page.hologram.setScale(scale);
  page.hologram.setMode(heroMode(screen));
  const who = el('div', 'who');
  const name = state.name || (state.id === null ? '' : llamaTag(state.id));
  if (name) who.append(pixelText(name, 'compass', NAME_SCALE, color, 'name'));
  who.append(say(standingOf(screen), 'line', 'standing'), say(statusOf(screen), 'note', 'status'));
  row.append(page.hologram.canvas, who);
  return row;
}

const heroMode = (screen: Screen) => {
  if (screen.kind === 'pad' || screen.kind === 'counting') return 'counting';
  return screen.kind === 'queue' || screen.kind === 'connecting' ? 'off' : 'on';
};

// The standing line: the word for the moment, in the light text.
function standingOf(screen: Screen): string {
  switch (screen.kind) {
    case 'waiting':
      return screen.onStream ? STRINGS.llinked : STRINGS.llinking;
    case 'connecting':
      return STRINGS.connecting;
    case 'hold':
      return screen.starting ? STRINGS.runStarting : '';
    case 'result':
      return screen.actual === screen.guess ? STRINGS.bullseye : '';
    default:
      return '';
  }
}

// The status line: what is happening in the room, on every tab. The open tab
// names itself, so the line never repeats it.
function statusOf(screen: Screen): string {
  switch (screen.kind) {
    case 'queue':
      return STRINGS.status.queue;
    case 'waiting':
      return STRINGS.status.waiting;
    case 'counting':
      return STRINGS.status.counting(screen.stage);
    case 'pad':
      return STRINGS.status.pad;
    case 'locked':
      return STRINGS.status.locked;
    case 'result':
      return STRINGS.status.result;
    case 'hold':
      return STRINGS.status.hold;
    case 'run_over':
      return STRINGS.status.run_over;
    case 'activity':
      if (screen.activity.kind === 'vote') return STRINGS.status.vote;
      return screen.activity.kind === 'stage' ? STRINGS.status.stage : '';
    default:
      return '';
  }
}
